import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import useAuthStore from '../store/useAuthStore';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const accountType = location.state?.accountType || 'CLIENT';
  const { login, register, googleLogin, isLoading } = useAuthStore();

  const [mode, setMode] = useState(location.state?.mode || 'login'); // 'login' | 'register'
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', fullName: '', accountType });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    const result = await googleLogin(response.credential, accountType);
    if (result.success) {
      navigate(result.isNew ? (accountType === 'PROVIDER' ? '/provider-setup' : '/client-setup') : '/home', { replace: true });
    } else {
      setError(result.error);
    }
  }, [googleLogin, accountType, navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });
    window.google.accounts.id.renderButton(
      document.getElementById('google-signin-btn'),
      { theme: 'filled_black', size: 'large', width: '100%', shape: 'pill', text: 'continue_with' }
    );
  }, [handleGoogleResponse]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'register' && form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const result = mode === 'login'
      ? await login(form.email, form.password)
      : await register(form);
    if (result.success) navigate(
      mode === 'register' && form.accountType === 'PROVIDER' ? '/provider-setup' :
      mode === 'register' && form.accountType === 'CLIENT' ? '/client-setup' :
      '/home',
      { replace: true }
    );
    else setError(result.error);
  };

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col px-6 py-10">
      <div className="mb-8 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-dark-subtext hover:text-dark-text">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <Logo size="sm" />
      </div>

      <div className="flex-1 max-w-sm mx-auto w-full">
        <h1 className="text-3xl font-bold text-dark-text mb-1" style={{ letterSpacing: '-0.5px' }}>
          {mode === 'login' ? 'Welcome back' : 'Create account'}
        </h1>
        <p className="text-dark-subtext mb-8 text-sm">
          {mode === 'login' ? 'Sign in to your Findr account' : `Join as a ${accountType.toLowerCase()}`}
        </p>

        {/* Google Sign-In */}
        <div className="mb-6">
          <div id="google-signin-btn" className="w-full flex justify-center" />
          {!GOOGLE_CLIENT_ID && (
            <button
              disabled
              className="w-full py-3.5 rounded-2xl bg-dark-surface border border-dark-border text-dark-text font-medium text-sm flex items-center justify-center gap-3 opacity-50"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google Sign-In (not configured)
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-dark-border" />
          <span className="text-dark-subtext text-xs">or continue with email</span>
          <div className="flex-1 h-px bg-dark-border" />
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === 'register' && (
            <div>
              <label className="block text-dark-subtext text-xs mb-1.5 font-medium">Full Name</label>
              <input
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                placeholder="Your full name"
                required
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple transition-colors"
              />
            </div>
          )}
          <div>
            <label className="block text-dark-subtext text-xs mb-1.5 font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@email.com"
              required
              className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 text-dark-text text-sm outline-none focus:border-brand-purple transition-colors"
            />
          </div>
          <div>
            <label className="block text-dark-subtext text-xs mb-1.5 font-medium">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                minLength={6}
                className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 pr-12 text-dark-text text-sm outline-none focus:border-brand-purple transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-subtext"
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-dark-subtext text-xs mb-1.5 font-medium">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full bg-dark-surface border border-dark-border rounded-xl px-4 py-3 pr-12 text-dark-text text-sm outline-none focus:border-brand-purple transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-subtext"
                >
                  {showConfirm ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-2xl bg-brand-gradient text-white font-semibold text-base mt-2 glow disabled:opacity-60"
          >
            {isLoading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {/* Toggle mode */}
        <p className="text-center text-dark-subtext text-sm mt-6">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => { setMode(m => m === 'login' ? 'register' : 'login'); setError(''); }}
            className="text-brand-purple font-semibold"
          >
            {mode === 'login' ? 'Sign Up' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
}
